import React, { useCallback, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogActions, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { CollectorScope, CollectorType } from 'graphql/types';
import { CollectorInstallInfo } from '../CollectorsListPage';
import { useUninstallCollector } from '../Install/hooks';
import { SettingLink } from './SettingLink';

type UserSettingDialogProps = {
  onClose: () => void;
  onSnackBarOpen: () => void;
  refetch: () => void;
  open: boolean;
  scope: CollectorScope;
  collectorInstallInfo: CollectorInstallInfo;
};

const useStyles = makeStyles((theme) => ({
  actions: {
    justifyContent: 'flex-start',
    padding: theme.spacing(0, 4, 1, 2)
  },
  loading: {
    display: 'flex',
    justifyContent: 'center'
  },
  title: {
    ...theme.typography.h4
  }
}));

export const UserSettingDialog: React.FC<UserSettingDialogProps> = ({
  onClose,
  onSnackBarOpen,
  open,
  scope,
  refetch,
  collectorInstallInfo
}: UserSettingDialogProps) => {
  const [isDisconnecting, setIsDisconnecting] = useState<boolean>(false);
  const { uninstallCollector } = useUninstallCollector();

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleDisconnect = useCallback(async () => {
    setIsDisconnecting(true);
    const collectorId = collectorInstallInfo.collectorItem.id;
    const dataSet = await uninstallCollector(collectorId, scope);
    if (dataSet?.data?.uninstallUserCollector?.collectorId) {
      onSnackBarOpen();
      refetch();
      handleClose();
    }
    setIsDisconnecting(false);
  }, [
    collectorInstallInfo.collectorItem.id,
    uninstallCollector,
    scope,
    onSnackBarOpen,
    handleClose,
    refetch
  ]);

  const classes = useStyles();
  const isPlugin = useMemo(
    () => collectorInstallInfo.collectorItem.type === CollectorType.Plugin,
    [collectorInstallInfo.collectorItem.type]
  );
  const collectorActionTitle = useMemo(
    () =>
      (isPlugin && <FormattedMessage id="collectorSetting.install" />) ||
      undefined,
    [isPlugin]
  );

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="simple-dialog-title"
      open={open}
      maxWidth="xs"
      data-testid="user-setting-dialog"
    >
      <form>
        <DialogTitle className={classes.title}>
          <FormattedMessage
            id="collectorSetting.title"
            values={{ prefix: collectorInstallInfo.collectorItem.name }}
          />
        </DialogTitle>
        <DialogActions className={classes.actions}>
          <SettingLink
            collectorItem={collectorInstallInfo.collectorItem}
            onClose={onClose}
            scope={scope}
            collectorActionTitle={collectorActionTitle}
            showIcon={!isPlugin}
          />
          {collectorInstallInfo.isInstalled &&
            collectorInstallInfo.collectorItem.type ===
              CollectorType.Plugin && (
              <Button
                onClick={handleDisconnect}
                data-testid="disconnect-button"
                color="secondary"
                disabled={isDisconnecting}
              >
                <FormattedMessage id="collectorSetting.disconnect" />
              </Button>
            )}

          <Button
            onClick={handleClose}
            data-testid="cancel-button"
            color="secondary"
          >
            <FormattedMessage id="collectorSetting.cancel" />
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
